import { ApiError, setToken, token } from './api.ts';
import { button } from './components.ts';
import { icon } from './icons.ts';
import { shell } from './shell.ts';
import { h } from './ui.ts';
import { inApp } from './talk-audio.ts';
import { agentView } from './views/agents/detail.ts';
import { agentsView } from './views/agents/list.ts';
import { knowledgeView } from './views/knowledge.ts';
import { activityView, approvalsView } from './views/misc.ts';
import { homeView } from './views/phone/home.ts';
import { phonePage } from './views/phone/kit.ts';
import { chatView } from './views/phone/chat.ts';
import { phoneLogin, talkView } from './views/phone/talk.ts';
import { projectPage } from './views/project-page.ts';
import { projectView } from './views/project.ts';
import { projectsView } from './views/projects.ts';
import { sessionView } from './views/session.ts';

/**
 * Hash router: #/agents · #/agent/<id> · #/projects · #/projects/<slug> · #/talk · #/chat · #/knowledge ·
 * #/project/<scope>/<tab>/<arg> (knowledge) · #/session/<id> · #/approvals · #/activity
 */
const app = document.getElementById('app')!;

/** The phone (the iOS app, or a phone-sized browser) gets its own screens without the sidebar (design-docs/0028). */
/** `?phone=1` (the installed web app opens with it) keeps the phone screens on a desktop too; `?phone=0` undoes it. */
function phoneWanted(): boolean {
  const asked = new URLSearchParams(location.search).get('phone');
  try {
    if (asked !== null) localStorage.setItem('phone', asked === '1' ? 'on' : 'off');
    return localStorage.getItem('phone') === 'on';
  } catch {
    return asked === '1';
  }
}
const phone = inApp() || phoneWanted() || window.matchMedia('(max-width: 520px)').matches;

const route = () => (location.hash || '#/').slice(1).split('/').slice(1);

/** Views both layouts share; null when the route is not one of them. */
async function shared(section: string, a: string): Promise<HTMLElement | null> {
  const arg = decodeURIComponent(a);
  if (section === 'agents') return agentsView();
  if (section === 'agent') return agentView(arg);
  if (section === 'projects') return arg ? projectPage(arg) : projectsView();
  if (section === 'session') return sessionView(arg);
  return null;
}

async function renderPhone() {
  document.body.classList.add('phone');
  const [section = '', a = ''] = route();
  try {
    if (section === 'talk') return app.replaceChildren(await talkView(a || undefined));
    if (section === 'chat') return app.replaceChildren(await chatView());
    const view = await shared(section, a);
    const back = section === 'agent' ? '#/agents' : section === 'projects' && a ? '#/projects' : '#/';
    app.replaceChildren(view ? phonePage(back, view) : await homeView());
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return app.replaceChildren(phoneLogin());
    app.replaceChildren(phonePage('#/', failure(err)));
  }
}

const ACTIVE: Record<string, string> = { agent: '#/agents', project: '#/knowledge', session: '#/knowledge' };

async function desktopView(section: string, a: string, rest: string[]): Promise<HTMLElement> {
  const view = await shared(section, a);
  if (view) return view;
  if (section === 'project') {
    // scope ids contain slashes: org/<org>/projects/<p>
    const parts = [a, ...rest];
    const projIdx = parts.indexOf('projects');
    const scopeLen = parts[0] === 'personal' ? 1 : projIdx >= 0 ? projIdx + 2 : 2;
    const scope = parts.slice(0, scopeLen).join('/');
    return projectView(scope, parts[scopeLen] ?? '', decodeURIComponent(parts.slice(scopeLen + 1).join('/')));
  }
  if (section === 'talk') return talkView(a || undefined);
  if (section === 'chat') return chatView();
  if (section === 'knowledge') return knowledgeView();
  if (section === 'approvals') return approvalsView();
  if (section === 'activity') return activityView();
  return agentsView();
}

async function render() {
  if (phone) return renderPhone();
  const [section = '', a = '', ...rest] = route();
  document.body.classList.toggle('talking', section === 'talk' || section === 'chat');
  let view: HTMLElement;
  try {
    view = await desktopView(section, a, rest);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return login();
    view = failure(err);
  }
  const active = section === 'projects' && a ? `#/projects/${a}` : (ACTIVE[section] ?? `#/${section || 'agents'}`);
  app.replaceChildren(shell(active, view));
  if (section !== 'talk' && section !== 'chat') document.getElementById('main')?.scrollTo(0, 0);
}

function failure(err: unknown): HTMLElement {
  return h(
    'div',
    { class: 'page' },
    h(
      'div',
      { class: 'empty' },
      h('b', {}, 'Can’t reach the assistant'),
      h('span', { class: 'mono' }, err instanceof Error ? err.message : String(err)),
      button('Try again', () => location.reload(), { variant: 'outline' }),
    ),
  );
}

function login() {
  const input = h('input', {
    class: 'input',
    type: 'password',
    placeholder: 'ASSISTANT_TOKEN from the daemon’s .env',
    'aria-label': 'Daemon token',
    autocomplete: 'current-password',
  });
  const go = () => {
    setToken(input.value.trim());
    void render();
  };
  input.addEventListener('keydown', (e) => (e as KeyboardEvent).key === 'Enter' && go());
  app.replaceChildren(
    h(
      'div',
      { class: 'login' },
      h(
        'div',
        { class: 'card login-card' },
        h('span', { class: 'brand-mark big' }, icon.mark(20)),
        h('h1', {}, 'Sign in'),
        h('p', { class: 'muted' }, 'Enter the daemon token to open the assistant.'),
        input,
        button('Continue', go, { size: 'lg', extra: 'glow' }),
      ),
    ),
  );
}

token();
window.addEventListener('hashchange', () => void render());
void render();
